import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Solution {
  id: number;
  industry: string;
  tagline: string;
  description: string;
  painPoints: {
    problem: string;
    solution: string;
  }[];
  image: string;
}

export default function Solutions() {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const solutions: Solution[] = [
    {
      id: 1,
      industry: t('solutions.healthcare'),
      tagline: t('healthcare.desc'),
      description: t('healthcare.desc'),
      painPoints: [
        {
          problem: t('healthcare.pain1.challenge'),
          solution: t('healthcare.pain1.solution') + ' - ' + t('healthcare.pain1.result')
        },
        {
          problem: t('healthcare.pain2.challenge'),
          solution: t('healthcare.pain2.solution') + ' - ' + t('healthcare.pain2.result')
        },
        {
          problem: t('healthcare.pain3.challenge'),
          solution: t('healthcare.pain3.solution') + ' - ' + t('healthcare.pain3.result')
        }
      ],
      image: 'https://public.readdy.ai/ai/img_res/9d826b5a3f80035d11708676056cfdab.jpg'
    },
    {
      id: 2,
      industry: t('solutions.education'),
      tagline: t('education.desc'),
      description: t('education.desc'),
      painPoints: [
        {
          problem: t('education.pain1.challenge'),
          solution: t('education.pain1.solution') + ' - ' + t('education.pain1.result')
        },
        {
          problem: t('education.pain2.challenge'),
          solution: t('education.pain2.solution') + ' - ' + t('education.pain2.result')
        },
        {
          problem: t('education.pain3.challenge'),
          solution: t('education.pain3.solution') + ' - ' + t('education.pain3.result')
        }
      ],
      image: 'https://readdy.ai/api/search-image?query=Educational%20technology%20consulting%20scene%20with%20diverse%20students%20and%20teachers%20using%20tablets%20and%20interactive%20displays%20in%20modern%20bright%20classroom%20environment%20collaborative%20learning%20atmosphere%20professional%20education%20consultants%20demonstrating%20digital%20solutions%20warm%20natural%20lighting&width=600&height=800&seq=education-consulting-002&orientation=portrait'
    },
    {
      id: 3,
      industry: t('solutions.retail'),
      tagline: t('retail.desc'),
      description: t('retail.desc'),
      painPoints: [
        {
          problem: t('retail.pain1.challenge'),
          solution: t('retail.pain1.solution') + ' - ' + t('retail.pain1.result')
        },
        {
          problem: t('retail.pain2.challenge'),
          solution: t('retail.pain2.solution') + ' - ' + t('retail.pain2.result')
        },
        {
          problem: t('retail.pain3.challenge'),
          solution: t('retail.pain3.solution') + ' - ' + t('retail.pain3.result')
        }
      ],
      image: 'https://readdy.ai/api/search-image?query=Retail%20business%20consulting%20scene%20with%20professional%20consultants%20advising%20store%20owners%20using%20modern%20point%20of%20sale%20systems%20and%20tablets%20in%20contemporary%20retail%20environment%20bright%20welcoming%20atmosphere%20showcasing%20customer%20service%20excellence%20and%20digital%20transformation&width=600&height=800&seq=retail-consulting-003&orientation=portrait'
    }
  ];

  return (
    <section id="solutions" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-[5%]">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-2 h-2 rounded-full bg-orange-500"></div>
            <span className="text-xs font-semibold tracking-wider text-gray-600 uppercase">{t('solutions.title')}</span>
          </div>
          <h2 className="text-5xl font-bold text-gray-900 mb-4">
            {t('solutions.subtitle')}
          </h2>
          <p className="text-lg text-gray-600">{t('solutions.learnMore')}</p>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {solutions.map((solution) => (
            <div key={solution.id} className="flex flex-col">
              <div
                className="group relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:scale-105 flex-shrink-0"
                style={{ aspectRatio: '3/4' }}
                onClick={() => setExpandedId(expandedId === solution.id ? null : solution.id)}
              >
                <div className="absolute inset-0 w-full h-full">
                  <img
                    src={solution.image}
                    alt={solution.industry}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="inline-block px-3 py-1 bg-[#14B8A6] rounded-full text-xs font-semibold mb-3">
                    {solution.industry}
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{solution.tagline}</h3>
                  <p className="text-sm leading-relaxed mb-4 text-gray-200">
                    {solution.description}
                  </p>
                  <span className="text-[#14B8A6] text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all whitespace-nowrap">
                    {expandedId === solution.id ? t('solutions.showLess') : t('solutions.learnMore')} <i className={`ri-arrow-${expandedId === solution.id ? 'up' : 'right'}-line`}></i>
                  </span>
                </div>
              </div>

              {expandedId === solution.id && (
                <div className="mt-6 bg-gray-50 rounded-2xl p-6 space-y-6 animate-fadeIn">
                  <h4 className="text-xl font-bold text-gray-900 mb-4">
                    How We Help {solution.industry} Organizations
                  </h4>
                  {solution.painPoints.map((point, index) => (
                    <div key={index} className="border-l-4 border-[#14B8A6] pl-4">
                      <div className="flex items-start gap-3 mb-2">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mt-1">
                          <i className="ri-close-line text-sm text-red-600"></i>
                        </div>
                        <p className="text-sm font-medium text-gray-900">
                          <strong>Challenge:</strong> {point.problem}
                        </p>
                      </div>
                      <div className="flex items-start gap-3 ml-9">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mt-1">
                          <i className="ri-check-line text-sm text-green-600"></i>
                        </div>
                        <p className="text-sm text-gray-700">
                          <strong className="text-[#14B8A6]">Our Solution:</strong> {point.solution}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div className="pt-4 border-t border-gray-200">
                    <a 
                      href="#contact" 
                      className="inline-flex items-center gap-2 px-6 py-3 bg-[#14B8A6] text-white rounded-lg font-medium hover:bg-[#0F9A8A] transition-colors whitespace-nowrap cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      {t('solutions.scheduleConsultation')} <i className="ri-calendar-line"></i>
                    </a>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
